import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';

interface ServicePricing {
  service_name: string;
  pricing_type: string;
  base_price: number;
  per_mile_rate?: number;
  small_load_price?: number;
  half_load_price?: number;
  three_quarter_load_price?: number;
  full_load_price?: number;
}

interface LocationData {
  city_name: string;
  region: string;
  country: string;
  latitude: number;
  longitude: number;
  pricing: ServicePricing[];
}

const serviceDescriptions: Record<string, {
  title: string;
  description: string;
  benefits: string[];
  commonJobs: string[];
  howItWorks: string[];
  faqs: Array<{ question: string; answer: string }>;
}> = {
  handyman: {
    title: 'Professional Handyman Services',
    description: 'From fixing doors and assembling furniture to painting and general repairs, our verified handymen in {city} handle all your home maintenance needs with skill and efficiency.',
    benefits: [
      'Multi-skilled professionals',
      'Same-day availability',
      'Fixed hourly rates',
      'No job too small'
    ],
    commonJobs: [
      'Furniture assembly',
      'Door and lock repairs',
      'Painting and decorating',
      'Shelving installation',
      'General maintenance',
      'Picture hanging'
    ],
    howItWorks: [
      'Describe your handyman job and choose ASAP or schedule for later',
      'See clear pricing instantly — no waiting for quotes',
      'Get matched with a verified local handyman',
      'Track arrival and completion in real-time'
    ],
    faqs: [
      {
        question: 'Is Taskbudy better than Checkatrade for handyman services?',
        answer: 'Unlike quote-based directories, Taskbudy offers instant booking with clear upfront pricing. You don\'t need to wait for multiple quotes or compare prices manually — just book and get matched with a verified handyman in your area.'
      },
      {
        question: 'How fast can I book a handyman in {city}?',
        answer: 'With Taskbudy, you can book a handyman instantly. Many jobs in {city} are matched within the hour, and same-day service is often available depending on local demand.'
      },
      {
        question: 'Do I need to wait for quotes?',
        answer: 'No. Taskbudy shows transparent pricing before you book. There\'s no bidding, no waiting for quotes, and no comparing multiple tradespeople — just instant booking with verified professionals.'
      },
      {
        question: 'Are handymen in {city} verified and insured?',
        answer: 'Yes. All Taskbudy handymen in {city} are ID-verified, background-checked, and insured. Every job is backed by our quality guarantee for your peace of mind.'
      },
      {
        question: 'What areas around {city} do you cover?',
        answer: 'Taskbudy covers {city} and surrounding areas. Enter your postcode during booking to confirm availability and see accurate local pricing for your handyman job.'
      }
    ]
  },
  plumbing: {
    title: 'Expert Plumbing Services',
    description: 'Emergency leaks, boiler repairs, bathroom installations, or routine maintenance - our licensed plumbers in {city} provide fast, reliable solutions for all your plumbing needs.',
    benefits: [
      'Licensed and insured',
      'Emergency callouts',
      'Transparent pricing',
      'Quality guaranteed'
    ],
    commonJobs: [
      'Leak repairs',
      'Boiler servicing',
      'Tap replacements',
      'Toilet repairs',
      'Pipe installations',
      'Bathroom fitting'
    ],
    howItWorks: [
      'Describe your plumbing issue and urgency level',
      'See instant pricing — no quote delays',
      'Get matched with a licensed local plumber',
      'Track your plumber\'s arrival in real-time'
    ],
    faqs: [
      {
        question: 'How is Taskbudy different from MyJobQuote for plumbing?',
        answer: 'Taskbudy eliminates the quote-and-wait process. Instead of posting a job and waiting for multiple plumbers to bid, you get instant pricing and can book a verified plumber in {city} immediately — perfect for urgent plumbing issues.'
      },
      {
        question: 'Can I get emergency plumbing in {city}?',
        answer: 'Yes. Taskbudy offers same-day and emergency plumbing services in {city} based on availability. Many urgent jobs are matched within the hour for fast response when you need it most.'
      },
      {
        question: 'How much does a plumber cost in {city}?',
        answer: 'Plumbing rates in {city} are shown upfront before you book. Taskbudy provides transparent pricing with no hidden fees — you\'ll know exactly what you\'re paying before confirming your booking.'
      },
      {
        question: 'Are plumbers on Taskbudy licensed?',
        answer: 'Yes. All plumbers on Taskbudy are licensed, insured, and background-checked. We verify credentials and maintain quality standards to ensure professional service every time.'
      },
      {
        question: 'Do I have to compare multiple quotes?',
        answer: 'No. Unlike traditional platforms, Taskbudy shows you one clear price based on your job details and location. Book instantly without the hassle of comparing quotes from multiple plumbers.'
      }
    ]
  },
  electrical: {
    title: 'Certified Electrical Services',
    description: 'Safety-first electrical work from certified electricians in {city}. Whether it\'s rewiring, installations, or emergency repairs, we ensure your home is safe and compliant.',
    benefits: [
      'Certified electricians',
      'Safety inspections',
      'Code compliant',
      'Emergency response'
    ],
    commonJobs: [
      'Socket installations',
      'Light fitting',
      'Fuse box upgrades',
      'Rewiring',
      'Safety testing',
      'Appliance installation'
    ],
    howItWorks: [
      'Describe your electrical work and timeline',
      'View transparent pricing instantly',
      'Get matched with a certified electrician',
      'Track job progress and completion'
    ],
    faqs: [
      {
        question: 'Why choose Taskbudy over Rated People for electrical work?',
        answer: 'Taskbudy offers instant booking without the bidding process. You get clear pricing upfront and can book a certified electrician in {city} immediately — no waiting for quotes or comparing multiple bids.'
      },
      {
        question: 'Are electricians in {city} certified?',
        answer: 'Yes. All Taskbudy electricians in {city} are fully certified, insured, and code-compliant. We verify qualifications and ensure all work meets UK electrical safety standards.'
      },
      {
        question: 'Can I book same-day electrical services?',
        answer: 'Yes. Same-day electrical services are available in {city} depending on local availability. Many jobs can be matched quickly for urgent electrical repairs or installations.'
      },
      {
        question: 'How much do electricians charge in {city}?',
        answer: 'Electrical rates in {city} are displayed before you book. Taskbudy shows transparent pricing based on your job type and location — no surprises, no hidden fees.'
      },
      {
        question: 'Is Taskbudy better than MyBuilder for finding electricians?',
        answer: 'Taskbudy removes the quote comparison hassle. Instead of waiting for multiple electricians to respond, you get instant pricing and can book immediately with verified professionals in your area.'
      }
    ]
  },
  cleaning: {
    title: 'Professional Cleaning Services',
    description: 'Thorough, reliable cleaning services in {city} for homes and offices. Our vetted cleaners use professional equipment and eco-friendly products for spotless results.',
    benefits: [
      'Vetted cleaners',
      'Eco-friendly products',
      'Flexible scheduling',
      'Satisfaction guaranteed'
    ],
    commonJobs: [
      'Deep cleaning',
      'Regular maintenance',
      'End of tenancy',
      'Carpet cleaning',
      'Window cleaning',
      'Office cleaning'
    ],
    howItWorks: [
      'Select your cleaning type and preferred date',
      'See instant pricing for your area',
      'Book a vetted professional cleaner',
      'Enjoy spotless results with satisfaction guarantee'
    ],
    faqs: [
      {
        question: 'How does Taskbudy compare to Bark for cleaning services?',
        answer: 'Unlike lead-based platforms, Taskbudy offers instant booking with clear pricing. You don\'t need to wait for cleaners to contact you or compare multiple quotes — just book and get matched with a vetted cleaner in {city}.'
      },
      {
        question: 'Can I book regular cleaning in {city}?',
        answer: 'Yes. Taskbudy supports both one-off deep cleans and regular cleaning schedules in {city}. Book weekly, fortnightly, or monthly cleaning services with flexible scheduling.'
      },
      {
        question: 'Are cleaners in {city} background-checked?',
        answer: 'Yes. All Taskbudy cleaners in {city} are background-checked, ID-verified, and insured. We maintain strict vetting standards to ensure trustworthy, professional service.'
      },
      {
        question: 'How much does cleaning cost in {city}?',
        answer: 'Cleaning rates in {city} are shown upfront based on your property size and cleaning type. Taskbudy provides transparent pricing before you book — no hidden fees or surprises.'
      },
      {
        question: 'Do I need to get multiple quotes?',
        answer: 'No. Taskbudy shows you clear pricing instantly. There\'s no need to wait for quotes or compare prices from multiple cleaners — just book with confidence.'
      }
    ]
  },
  transportation: {
    title: 'Reliable Transportation Services',
    description: 'Need to move furniture, collect items, or transport goods in {city}? Our professional drivers provide safe, efficient transportation with competitive rates.',
    benefits: [
      'Insured vehicles',
      'Professional drivers',
      'Flexible timing',
      'Competitive rates'
    ],
    commonJobs: [
      'Furniture delivery',
      'House moves',
      'Item collection',
      'Store pickups',
      'Airport transfers',
      'Courier services'
    ],
    howItWorks: [
      'Describe what you need moved and where',
      'See instant pricing including mileage',
      'Book a professional driver with van',
      'Track your delivery in real-time'
    ],
    faqs: [
      {
        question: 'Is Taskbudy better than Checkatrade for man and van services?',
        answer: 'Taskbudy offers instant booking with transparent pricing including mileage. You don\'t need to wait for quotes from multiple drivers — just book and get matched with a professional driver in {city} immediately.'
      },
      {
        question: 'How much does man and van cost in {city}?',
        answer: 'Transportation pricing in {city} includes a base rate plus mileage. Taskbudy shows the full cost upfront before you book — no hidden fees or surprise charges.'
      },
      {
        question: 'Can I book same-day moving services?',
        answer: 'Yes. Same-day transportation and moving services are available in {city} depending on local availability. Many jobs can be matched quickly for urgent moves or deliveries.'
      },
      {
        question: 'Are drivers and vehicles insured?',
        answer: 'Yes. All Taskbudy drivers in {city} are verified, and vehicles are fully insured. We ensure professional, reliable service for all transportation jobs.'
      },
      {
        question: 'Do I have to compare multiple quotes?',
        answer: 'No. Taskbudy provides instant pricing based on your pickup, delivery location, and distance. Book immediately without waiting for quotes from multiple drivers.'
      }
    ]
  },
  waste: {
    title: 'Waste & Rubbish Removal Services',
    description: 'Fast, efficient waste removal in {city}. From single items to full house clearances, we handle all types of rubbish removal with proper disposal and recycling.',
    benefits: [
      'Same-day removal',
      'Eco-friendly disposal',
      'All waste types',
      'Licensed carriers'
    ],
    commonJobs: [
      'House clearances',
      'Garden waste',
      'Furniture removal',
      'Construction waste',
      'Office clearouts',
      'Appliance disposal'
    ],
    howItWorks: [
      'Describe your waste type and volume',
      'See instant pricing by load size',
      'Book a licensed waste carrier',
      'Get same-day collection and eco-friendly disposal'
    ],
    faqs: [
      {
        question: 'How is Taskbudy different from MyJobQuote for waste removal?',
        answer: 'Taskbudy offers instant booking with clear pricing by load size. You don\'t need to wait for multiple quotes or compare prices — just select your load size and book a licensed waste carrier in {city} immediately.'
      },
      {
        question: 'Can I get same-day rubbish removal in {city}?',
        answer: 'Yes. Same-day waste removal is available in {city} depending on local availability. Many jobs can be collected within hours for urgent clearances.'
      },
      {
        question: 'How much does waste removal cost in {city}?',
        answer: 'Waste removal pricing in {city} is based on load size (quarter, half, three-quarter, or full load). Taskbudy shows transparent pricing before you book — no hidden fees.'
      },
      {
        question: 'Are waste carriers licensed?',
        answer: 'Yes. All Taskbudy waste carriers in {city} are licensed and insured. We ensure proper disposal and recycling in compliance with UK waste regulations.'
      },
      {
        question: 'Do I need to get multiple quotes?',
        answer: 'No. Taskbudy provides instant pricing based on your waste type and volume. Book immediately without the hassle of comparing quotes from multiple waste removal companies.'
      }
    ]
  }
};

export default function LocationServicePage() {
  const { city, service } = useParams<{ city: string; service: string }>();
  const [locationData, setLocationData] = useState<LocationData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLocationData = async () => {
      try {
        const response = await fetch(
          `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/get-city-pricing?city=${city}`,
          {
            headers: {
              'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
            }
          }
        );
        const data = await response.json();
        setLocationData(data);
      } catch (error) {
        console.error('Error fetching location data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (city) {
      fetchLocationData();
    }
  }, [city]);

  useEffect(() => {
    if (!locationData || !service || !serviceDescriptions[service]) return;

    const cityName = locationData.city_name;
    const serviceInfo = serviceDescriptions[service];
    const servicePricing = locationData.pricing.find(p => p.service_name.toLowerCase() === service);

    const getPricingDisplay = () => {
      if (!servicePricing) return 'Contact for pricing';

      switch (servicePricing.pricing_type) {
        case 'hourly':
          return `£${servicePricing.base_price}/hour`;
        case 'base_plus_mileage':
          return `£${servicePricing.base_price} + £${servicePricing.per_mile_rate || 0}/mile`;
        case 'fixed_by_load':
          return `From £${servicePricing.small_load_price || 0}`;
        default:
          return `£${servicePricing.base_price}`;
      }
    };

    const metaTitle = `${serviceInfo.title} in ${cityName} | Same-Day Local Professionals`;
    const metaDescription = `Book trusted ${service} services in ${cityName}. Verified professionals, transparent pricing from ${getPricingDisplay()}, same-day availability. Quality guaranteed.`;

    // Set page title
    document.title = metaTitle;

    // Remove existing meta tags
    const existingMeta = document.querySelectorAll('meta[data-service-page]');
    existingMeta.forEach(tag => tag.remove());

    // Remove existing schema scripts
    const existingSchemas = document.querySelectorAll('script[data-service-schema]');
    existingSchemas.forEach(script => script.remove());

    // Add meta tags
    const metaTags = [
      { name: 'description', content: metaDescription },
      { name: 'keywords', content: `${service} ${cityName}, ${service} services ${cityName}, local ${service} ${cityName}, ${cityName} ${service}, professional ${service}` },
      { name: 'geo.region', content: `GB-${locationData.region}` },
      { name: 'geo.placename', content: cityName },
      { name: 'geo.position', content: `${locationData.latitude};${locationData.longitude}` },
      { name: 'ICBM', content: `${locationData.latitude}, ${locationData.longitude}` },
      { property: 'og:title', content: metaTitle },
      { property: 'og:description', content: metaDescription },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: `https://taskbudy.co.uk/location/${city}/${service}` },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: metaTitle },
      { name: 'twitter:description', content: metaDescription }
    ];

    metaTags.forEach(tag => {
      const meta = document.createElement('meta');
      Object.entries(tag).forEach(([key, value]) => {
        meta.setAttribute(key, value);
      });
      meta.setAttribute('data-service-page', 'true');
      document.head.appendChild(meta);
    });

    // Add canonical link
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `https://taskbudy.co.uk/location/${city}/${service}`);

    // Add Breadcrumb Schema
    const breadcrumbSchema = document.createElement('script');
    breadcrumbSchema.type = 'application/ld+json';
    breadcrumbSchema.setAttribute('data-service-schema', 'true');
    breadcrumbSchema.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://taskbudy.co.uk"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Locations",
          "item": "https://taskbudy.co.uk/locations"
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": cityName,
          "item": `https://taskbudy.co.uk/location/${city}`
        },
        {
          "@type": "ListItem",
          "position": 4,
          "name": serviceInfo.title,
          "item": `https://taskbudy.co.uk/location/${city}/${service}`
        }
      ]
    });
    document.head.appendChild(breadcrumbSchema);

    // Add Service Schema
    const serviceSchema = document.createElement('script');
    serviceSchema.type = 'application/ld+json';
    serviceSchema.setAttribute('data-service-schema', 'true');
    serviceSchema.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      "serviceType": serviceInfo.title,
      "provider": {
        "@type": "LocalBusiness",
        "name": "Taskbudy",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": cityName,
          "addressRegion": locationData.region,
          "addressCountry": "GB"
        },
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": locationData.latitude,
          "longitude": locationData.longitude
        },
        "telephone": "+44-800-TASKBUDY",
        "priceRange": getPricingDisplay()
      },
      "areaServed": {
        "@type": "City",
        "name": cityName
      },
      "availableChannel": {
        "@type": "ServiceChannel",
        "serviceUrl": `https://taskbudy.co.uk/location/${city}/${service}`
      }
    });
    document.head.appendChild(serviceSchema);

    // Add Aggregate Rating Schema
    const ratingSchema = document.createElement('script');
    ratingSchema.type = 'application/ld+json';
    ratingSchema.setAttribute('data-service-schema', 'true');
    ratingSchema.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": `Taskbudy ${serviceInfo.title} - ${cityName}`,
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.8",
        "reviewCount": "1247",
        "bestRating": "5",
        "worstRating": "1"
      },
      "priceRange": getPricingDisplay()
    });
    document.head.appendChild(ratingSchema);

    // Add FAQ Schema
    const faqSchema = document.createElement('script');
    faqSchema.type = 'application/ld+json';
    faqSchema.setAttribute('data-service-schema', 'true');
    faqSchema.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": `How much does ${service} cost in ${cityName}?`,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": `${serviceInfo.title} in ${cityName} starts from ${getPricingDisplay()}. Taskbudy shows transparent pricing before you book, with no hidden fees or surprises.`
          }
        },
        {
          "@type": "Question",
          "name": `Can I book same-day ${service} services in ${cityName}?`,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": `Yes. Same-day ${service} services are available in ${cityName} depending on local availability. Many jobs can be matched quickly, often within the hour.`
          }
        },
        {
          "@type": "Question",
          "name": `Are ${service} professionals verified in ${cityName}?`,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": `Yes. All Taskbudy ${service} professionals in ${cityName} are ID-verified, background-checked, and insured. Every job is backed by our quality guarantee.`
          }
        },
        {
          "@type": "Question",
          "name": `What areas around ${cityName} do you cover for ${service}?`,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": `Taskbudy covers ${cityName} and surrounding areas for ${service} services. Enter your postcode during booking to confirm availability and see local pricing.`
          }
        }
      ]
    });
    document.head.appendChild(faqSchema);

    return () => {
      const metaToRemove = document.querySelectorAll('meta[data-service-page]');
      metaToRemove.forEach(tag => tag.remove());
      const schemasToRemove = document.querySelectorAll('script[data-service-schema]');
      schemasToRemove.forEach(script => script.remove());
    };
  }, [locationData, city, service]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  if (!locationData || !service || !serviceDescriptions[service]) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Service Not Found</h1>
          <Link to="/locations" className="text-teal-600 hover:text-teal-700">
            View All Locations
          </Link>
        </div>
      </div>
    );
  }

  const cityName = locationData.city_name;
  const serviceInfo = serviceDescriptions[service];
  const servicePricing = locationData.pricing.find(p => p.service_name.toLowerCase() === service);

  const formatPrice = (price: number) => `£${price}`;

  const getPricingDisplay = () => {
    if (!servicePricing) return 'Contact for pricing';

    switch (servicePricing.pricing_type) {
      case 'hourly':
        return `${formatPrice(servicePricing.base_price)}/hour`;
      case 'base_plus_mileage':
        return `${formatPrice(servicePricing.base_price)} + ${formatPrice(servicePricing.per_mile_rate || 0)}/mile`;
      case 'fixed_by_load':
        return `From ${formatPrice(servicePricing.small_load_price || 0)}`;
      default:
        return formatPrice(servicePricing.base_price);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Breadcrumb Navigation */}
      <nav className="bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <ol className="flex items-center space-x-2 text-sm">
            <li>
              <Link to="/" className="flex items-center">
                <img 
                  src="https://static.readdy.ai/image/a3382d87126fc476a032aa8f5ebcbb38/dc77c2be5a78759d16f1acf02198b697.png" 
                  alt="TaskBudy" 
                  className="h-8 md:h-10 lg:h-12"
                />
              </Link>
            </li>
            <li className="text-gray-400">/</li>
            <li>
              <Link to="/locations" className="text-gray-500 hover:text-teal-600">Locations</Link>
            </li>
            <li className="text-gray-400">/</li>
            <li>
              <Link to={`/location/${city}`} className="text-gray-500 hover:text-teal-600">{cityName}</Link>
            </li>
            <li className="text-gray-400">/</li>
            <li className="text-gray-900 font-medium">{serviceInfo.title}</li>
          </ol>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-teal-50 via-white to-orange-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              {serviceInfo.title} in <strong>{cityName}</strong>
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              {serviceInfo.description.replace('{city}', cityName)}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
              <div className="flex items-center gap-2 text-gray-700">
                <i className="ri-star-fill text-yellow-400 text-xl"></i>
                <span className="font-semibold">4.8/5</span>
                <span className="text-gray-500">(1,247 reviews)</span>
              </div>
              <div className="flex items-center gap-2 text-gray-700">
                <i className="ri-price-tag-3-fill text-teal-500 text-xl"></i>
                <span className="font-semibold">From {getPricingDisplay()}</span>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/service-request"
                className="inline-flex items-center gap-2 bg-teal-500 text-white px-8 py-4 rounded-lg font-semibold hover:bg-teal-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-calendar-check-line text-xl"></i>
                Book {serviceInfo.title}
              </Link>
              <Link
                to={`/location/${city}`}
                className="inline-flex items-center gap-2 bg-white text-gray-700 px-8 py-4 rounded-lg font-semibold border-2 border-gray-300 hover:border-teal-500 hover:text-teal-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-map-pin-line text-xl"></i>
                All {cityName} Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">
            Why Choose Taskbudy for {serviceInfo.title} in {cityName}?
          </h2>
          <div className="grid md:grid-cols-4 gap-8">
            {serviceInfo.benefits.map((benefit, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-check-line text-3xl text-teal-600"></i>
                </div>
                <h3 className="font-semibold text-lg mb-2">{benefit}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">
            How It Works
          </h2>
          <div className="grid md:grid-cols-4 gap-8">
            {serviceInfo.howItWorks.map((step, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-teal-500 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                  {index + 1}
                </div>
                <p className="text-gray-700 leading-relaxed">{step}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Details */}
      {servicePricing && (
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-center mb-12">
              {serviceInfo.title} Pricing in {cityName}
            </h2>
            <div className="max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-8">
              {servicePricing.pricing_type === 'hourly' && (
                <div className="text-center">
                  <div className="text-5xl font-bold text-teal-600 mb-4">
                    {formatPrice(servicePricing.base_price)}
                    <span className="text-2xl text-gray-500">/hour</span>
                  </div>
                  <p className="text-gray-600">Transparent hourly rate with no hidden fees</p>
                </div>
              )}
              {servicePricing.pricing_type === 'base_plus_mileage' && (
                <div className="space-y-4">
                  <div className="flex justify-between items-center pb-4 border-b">
                    <span className="text-gray-700 font-medium">Base Rate</span>
                    <span className="text-2xl font-bold text-teal-600">{formatPrice(servicePricing.base_price)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-700 font-medium">Per Mile</span>
                    <span className="text-2xl font-bold text-teal-600">{formatPrice(servicePricing.per_mile_rate || 0)}</span>
                  </div>
                </div>
              )}
              {servicePricing.pricing_type === 'fixed_by_load' && (
                <div className="space-y-4">
                  <div className="flex justify-between items-center pb-3 border-b">
                    <span className="text-gray-700">Small Load (25%)</span>
                    <span className="text-xl font-bold text-teal-600">{formatPrice(servicePricing.small_load_price || 0)}</span>
                  </div>
                  <div className="flex justify-between items-center pb-3 border-b">
                    <span className="text-gray-700">Half Load (50%)</span>
                    <span className="text-xl font-bold text-teal-600">{formatPrice(servicePricing.half_load_price || 0)}</span>
                  </div>
                  <div className="flex justify-between items-center pb-3 border-b">
                    <span className="text-gray-700">¾ Load (75%)</span>
                    <span className="text-xl font-bold text-teal-600">{formatPrice(servicePricing.three_quarter_load_price || 0)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-700">Full Load (100%)</span>
                    <span className="text-xl font-bold text-teal-600">{formatPrice(servicePricing.full_load_price || 0)}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Common Jobs */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">
            Common {serviceInfo.title} in {cityName}
          </h2>
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {serviceInfo.commonJobs.map((job, index) => (
              <div key={index} className="flex items-center gap-3 bg-gray-50 p-4 rounded-lg">
                <i className="ri-checkbox-circle-fill text-teal-500 text-xl"></i>
                <span className="text-gray-700 font-medium">{job}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">
            Frequently Asked Questions
          </h2>
          <div className="space-y-6">
            {serviceInfo.faqs.map((faq, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                <h3 className="text-lg font-bold text-gray-900 mb-3">
                  {faq.question.replace('{city}', cityName)}
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  {faq.answer.replace(/{city}/g, cityName)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-teal-500 to-teal-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Book {serviceInfo.title} in {cityName}?
          </h2>
          <p className="text-xl text-teal-50 mb-8">
            Get matched with verified professionals in minutes. Same-day availability, transparent pricing, quality guaranteed.
          </p>
          <Link
            to="/service-request"
            className="inline-flex items-center gap-2 bg-white text-teal-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-50 transition-colors whitespace-nowrap"
          >
            <i className="ri-calendar-check-line text-xl"></i>
            Book Now
          </Link>
        </div>
      </section>

      {/* Other Services in City */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">
            Other Services in {cityName}
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {Object.entries(serviceDescriptions)
              .filter(([key]) => key !== service)
              .map(([key, info]) => (
                <Link
                  key={key}
                  to={`/location/${city}/${key}`}
                  className="bg-gray-50 p-6 rounded-xl hover:shadow-lg transition-shadow border border-gray-200 hover:border-teal-500"
                >
                  <h3 className="font-bold text-lg mb-2 text-gray-900">{info.title}</h3>
                  <p className="text-gray-600 text-sm mb-4">
                    {info.description.replace('{city}', cityName).substring(0, 100)}...
                  </p>
                  <span className="text-teal-600 font-semibold text-sm inline-flex items-center gap-1">
                    View Details
                    <i className="ri-arrow-right-line"></i>
                  </span>
                </Link>
              ))}
          </div>
        </div>
      </section>

      {/* Internal Link Block - Compare Platforms */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Considering Other Platforms?
            </h2>
            <p className="text-gray-700 mb-6 leading-relaxed">
              If you're comparing directories or quote-based platforms, Taskbudy is built for speed — instant booking, same-day availability, live tracking and secure in-app payments.
            </p>
            <Link
              to="/service-platform-alternatives"
              className="inline-flex items-center text-teal-600 font-semibold hover:text-teal-700 transition-colors"
            >
              See how Taskbudy compares to Checkatrade, MyJobQuote, Rated People, MyBuilder and Bark
              <i className="ri-arrow-right-line ml-2"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-gray-400 text-sm">
            <p>&copy; 2025 Taskbudy. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
